'use client';

import {Currency, money, parseFloatingNumber} from '@rocket-ui/tools';
import isNil from 'lodash/fp/isNil';
import React, {useState} from 'react';
import {Assign} from 'utility-types';
import {numericTransformer} from './transformations';

interface Props {
	price?: string;
	fractionDigits?: number;
	placeholder?: string;
	onChange: (value: string | null) => void;
	currency?: Currency;
	autoFocus?: boolean;
	negativeAllowed?: boolean;
}

export type PriceInputSectionProps = Assign<Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value'>, Props>;

export const PriceInput = React.forwardRef<HTMLInputElement, PriceInputSectionProps>(function PriceInput(
	{
		price,
		fractionDigits = 0,
		placeholder = '0',
		onChange,
		autoFocus,
		currency = Currency.RUB,
		negativeAllowed,
		disabled,
	},
	ref,
) {
	const [inFocus, setInFocus] = useState(false);

	const handlePriceChange = async (event: React.ChangeEvent<HTMLInputElement>) =>
		numericTransformer(event.currentTarget.value.replace(/ */g, ''), onChange, {fractionDigits, negativeAllowed});

	const displayValue = isNil(price)
		? ''
		: inFocus
		? price
		: parseFloatingNumber(price).toLocaleString('ru-RU', {
				minimumFractionDigits: fractionDigits,
				maximumFractionDigits: fractionDigits,
		  });

	return (
		<span className={'relative flex items-baseline w-full h-full overflow-hidden'}>
			<input
				value={displayValue}
				onChange={handlePriceChange}
				className={'w-full h-full p-0 border-0 bg-transparent rounded-md placeholder:text-black/30'}
				placeholder={placeholder}
				maxLength={15}
				onFocus={() => setInFocus(true)}
				onBlur={() => setInFocus(false)}
				autoFocus={autoFocus}
				disabled={disabled}
				ref={ref}
			/>
			{isNil(price) ? null : (
				<span className={'absolute inset-0 flex items-center gap-1 pointer-events-none'}>
					<span className={'invisible'}>{displayValue || placeholder}</span>
					<span className={isNil(displayValue) || displayValue === '' ? 'text-black/[.3]' : ''}>
						{money.currencySymbolByCode[currency]}
					</span>
				</span>
			)}
		</span>
	);
});
